// MobilePushToggle — reusable iOS/Android web-push opt-in row for mobile UIs.
// Uses the existing VAPID push infra (utils/alerts.js). On iOS, Apple only allows
// web push when the app is installed to the Home Screen, so we detect standalone
// mode and guide the user to install first.
import { useState, useEffect, useCallback } from "react";
import { BellRing, BellOff, Share, Loader2, CheckCircle2 } from "lucide-react";
import { pushSupported, pushStatus, pushSubscribe, pushUnsubscribe, pushTest } from "../utils/alerts";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.navigator.standalone === true ||
    window.matchMedia?.("(display-mode: standalone)")?.matches === true);

export default function MobilePushToggle({ c = {} }) {
  const col = {
    accent: c.accent || "#38bdf8",
    text: c.text || "#f5f5f7",
    sub: c.sub || "rgba(245,245,247,0.55)",
    card: c.card || "rgba(255,255,255,0.05)",
    border: c.border || "rgba(255,255,255,0.10)",
    green: c.green || "#30d158",
    red: c.red || "#ff453a",
  };
  const [supported, setSupported] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState("default");
  const [busy, setBusy] = useState(false);
  const [tested, setTested] = useState(false);
  const iosNeedsInstall = isIOS() && !isStandalone();

  const refresh = useCallback(async () => {
    const ok = await pushSupported();
    setSupported(ok);
    if (!ok) return;
    try {
      const s = await pushStatus();
      setSubscribed(!!s.subscribed);
      setPermission(s.permission || "default");
    } catch {}
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = async () => {
    setBusy(true);
    try {
      await pushSubscribe();
      await refresh();
    } catch (e) {
      // permission denied or unsupported — surface via state
      await refresh();
    }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try { await pushUnsubscribe(); } catch {}
    await refresh();
    setBusy(false);
  };

  const sendTest = async () => {
    setBusy(true);
    try { await pushTest(); setTested(true); setTimeout(() => setTested(false), 3000); } catch {}
    setBusy(false);
  };

  if (!supported && !iosNeedsInstall) {
    return (
      <div data-testid="mobile-push-unsupported" style={{ fontSize: 11, color: col.sub, padding: "10px 4px", lineHeight: 1.4 }}>
        Push notifications aren’t supported on this browser.
      </div>
    );
  }

  return (
    <div data-testid="mobile-push-toggle" style={{
      display: "flex", flexDirection: "column", gap: 10, padding: 12, borderRadius: 14,
      background: col.card, border: `1px solid ${col.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
          background: subscribed ? `${col.green}22` : `${col.accent}1f`, color: subscribed ? col.green : col.accent,
        }}>
          {subscribed ? <BellRing size={17} /> : <BellOff size={17} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: col.text }}>Push Notifications</div>
          <div style={{ fontSize: 10.5, color: col.sub, lineHeight: 1.4 }}>
            {subscribed ? "On — risk alerts & fills arrive on this device"
              : "Get risk alerts, order fills & Zi picks on your phone"}
          </div>
        </div>
      </div>

      {iosNeedsInstall ? (
        <div data-testid="mobile-push-ios-hint" style={{
          fontSize: 11, color: col.sub, lineHeight: 1.5, padding: "9px 11px", borderRadius: 10,
          background: `${col.accent}12`, border: `1px solid ${col.accent}33`,
        }}>
          <b style={{ color: col.text }}>iPhone / iPad:</b> tap <Share size={12} style={{ verticalAlign: "-2px" }} />{" "}
          <b>Share</b> → <b>Add to Home Screen</b>, then open Omega ZI from the new icon to enable push.
        </div>
      ) : subscribed ? (
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" data-testid="mobile-push-test-btn" onClick={sendTest} disabled={busy}
            style={{
              flex: 1, padding: "9px", borderRadius: 10, cursor: busy ? "default" : "pointer",
              border: `1px solid ${col.border}`, background: "transparent", color: col.text, fontSize: 12, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            {busy ? <Loader2 size={13} className="spin" /> : tested ? <CheckCircle2 size={13} color={col.green} /> : null}
            {tested ? "Sent" : "Send test"}
          </button>
          <button type="button" data-testid="mobile-push-disable-btn" onClick={disable} disabled={busy}
            style={{
              flex: 1, padding: "9px", borderRadius: 10, cursor: busy ? "default" : "pointer",
              border: `1px solid ${col.red}44`, background: `${col.red}14`, color: col.red, fontSize: 12, fontWeight: 700,
            }}>
            Turn off
          </button>
        </div>
      ) : (
        <button type="button" data-testid="mobile-push-enable-btn" onClick={enable} disabled={busy || permission === "denied"}
          style={{
            width: "100%", padding: "11px", borderRadius: 12, cursor: (busy || permission === "denied") ? "default" : "pointer",
            border: "none", background: permission === "denied" ? `${col.red}22` : col.accent,
            color: permission === "denied" ? col.red : "#fff", fontSize: 13, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
          {busy && <Loader2 size={14} className="spin" />}
          {permission === "denied" ? "Blocked — enable in device settings" : "Enable Push Notifications"}
        </button>
      )}
    </div>
  );
}
